import * as React from 'react';
import {
  AddTile,
  BottomSheet,
  BottomSheetContent,
  Button,
  Stack,
  VehicleCard,
} from '@relay-towing/ui';
import { CarSimple, Plus } from '@phosphor-icons/react';
import type { ScannedVin } from '../../hooks/useScanVin';
import {
  isBlankVehicle,
  makeOf,
  type VehicleRecord,
  type VehicleValues,
} from '../../lib/vehicleSchema';
import type { Adder } from '../shell/adder';
import { VehicleSheet } from '../shell/VehicleSheet';
import { VinScanSheet } from '../shell/VinScanSheet';

/** Which sheet is up: the chooser, the VIN camera, or the vehicle's form. */
type Step = 'choose' | 'scan' | 'form';

/**
 * The call's Vehicle section, as the Figma's Add Vehicle rows draw it. A
 * call with nothing on its vehicle is the dashed Add vehicle tile
 * (865:32246), which asks By VIN or Add manually (865:32297). Add manually
 * opens the Add Vehicle sheet, with Back to the question (865:32350); By
 * VIN reads one off the camera first (460:83400) and opens the same sheet
 * filled from what it decodes to (865:33727). Once the vehicle has
 * anything on it the section is a card of its facts (865:32414), which a
 * tap opens as the Vehicle sheet with Update and Delete Vehicle (865:32488,
 * 865:32575).
 *
 * A member who may not change the call sees the card and nothing to tap;
 * the server refuses the change anyway (assertVehicleChangeable, the same
 * question CallPhotos' `editable` answers).
 */
export const CallVehicle = React.forwardRef<
  Adder,
  {
    vehicle: (Omit<VehicleRecord, 'id'> & { id?: string | null }) | null;
    /** May change the vehicle; the server refuses anyone else. */
    editable: boolean;
  }
>(function CallVehicle({ vehicle, editable }, ref) {
  const [step, setStep] = React.useState<Step | null>(null);
  // By VIN or by hand, as the Add vehicle tile asks.
  React.useImperativeHandle(ref, () => ({ add: () => setStep('choose') }), []);
  const [seed, setSeed] = React.useState<Partial<VehicleValues>>();
  const [typingVin, setTypingVin] = React.useState(false);
  const [fromChooser, setFromChooser] = React.useState(false);
  // Only a vehicle the server has a row for can be changed through it.
  const record: VehicleRecord | null =
    editable && vehicle?.id ? { ...vehicle, id: vehicle.id } : null;
  const close = (next: boolean) => !next && setStep(null);

  const toForm = (options: {
    seed?: Partial<VehicleValues>;
    typingVin?: boolean;
    fromChooser?: boolean;
  }) => {
    setSeed(options.seed);
    setTypingVin(options.typingVin ?? false);
    setFromChooser(options.fromChooser ?? false);
    setStep('form');
  };

  const scanned = ({ vin, vehicle: decoded }: ScannedVin) =>
    toForm({
      seed: {
        vin,
        ...(decoded?.make ? { make: decoded.make } : {}),
        ...(decoded?.model ? { model: decoded.model } : {}),
        ...(decoded?.year ? { year: String(decoded.year) } : {}),
      },
    });

  if (record && isBlankVehicle(record))
    return (
      <>
        <AddTile width="block" onClick={() => setStep('choose')}>
          Add vehicle
        </AddTile>
        <BottomSheet open={step === 'choose'} onOpenChange={close}>
          <BottomSheetContent title="Add Vehicle">
            <Stack gap={4}>
              <Button
                variant="secondary"
                size="xl"
                pill
                fullWidth
                leadingIcon={<CarSimple />}
                onClick={() => setStep('scan')}
              >
                By VIN
              </Button>
              <Button
                variant="secondary"
                size="xl"
                pill
                fullWidth
                leadingIcon={<Plus />}
                onClick={() => toForm({ fromChooser: true })}
              >
                Add manually
              </Button>
            </Stack>
          </BottomSheetContent>
        </BottomSheet>
        <VinScanSheet
          open={step === 'scan'}
          onOpenChange={close}
          onRead={scanned}
          onEnterManually={(vin) =>
            toForm({ seed: vin ? { vin } : undefined, typingVin: true })
          }
        />
        <VehicleSheet
          open={step === 'form'}
          onOpenChange={close}
          vehicle={record}
          title="Add Vehicle"
          submitLabel="Add"
          seed={seed}
          typingVin={typingVin}
          onBack={fromChooser ? () => setStep('choose') : undefined}
        />
      </>
    );

  return (
    <>
      <VehicleCard
        vin={vehicle?.vin}
        make={makeOf(vehicle)}
        model={vehicle?.vehicleModel}
        year={vehicle?.vehicleYear}
        color={vehicle?.vehicleAttributes?.color}
        // As sent: a driver-only member is not sent an impound vehicle's
        // miles, and a field the server left out has no row, not a dash.
        odometer={vehicle?.odometer}
        plate={vehicle?.licensePlate}
        onOpen={record ? () => setStep('form') : undefined}
      />
      {record ? (
        <VehicleSheet
          open={step === 'form'}
          onOpenChange={close}
          vehicle={record}
          deletable
        />
      ) : null}
    </>
  );
});
